import React, { useState } from 'react';
import { Radio, RadioChangeEvent, DatePicker, Form } from 'antd';
import { DebtStatusCode, PaymentType } from 'models';
import { formatCurrency } from 'utils';
import { InputCommon, LabelWithRequired, QuickViewProductsModal, QuickViewTransactionsModal } from 'components/common';
import dayjs, { Dayjs } from 'dayjs';
import { DebtPaymentInfoProps } from 'types';

export const DebtStatusTag: React.FC<{ status?: string }> = ({ status }) => {
  let statusColor: string = '';
  let statusText: string = '';

  switch (status) {
    case DebtStatusCode.WAITING:
      statusColor = 'bg-[var(--sematicSuccessColor500)] text-white';
      statusText = 'Đợi thu';
      break;
    case DebtStatusCode.COMPLETED:
      statusColor = 'bg-[var(--textBlack56)] text-white';
      statusText = 'Hoàn tất';
      break;
    case DebtStatusCode.CONFIRMING:
      statusColor = 'bg-[var(--sematicErrorColor600)] text-white';
      statusText = 'Đợi xác nhận';
      break;
    case DebtStatusCode.REFUND:
      statusColor = 'bg-[var(--sematicErrorColor500)] text-white';
      statusText = 'Phải trả';
      break;
    case DebtStatusCode.OVERDUE:
      statusColor = 'bg-[var(--sematicErrorColor500)] text-white';
      statusText = 'Trễ hẹn';
      break;
    default:
      statusColor = 'bg-[var(--textBlack8)] text-[var(--textBlack56)]';
      statusText = 'Không xác định';
  }

  return <div className={`inline-block text-sm px-4 py-1 rounded-full ${statusColor}`}>{statusText}</div>;
};

export const DebtSectionHeader: React.FC<{ title: string; className?: string }> = ({ title, className }) => (
  <h3 className={`leading-[24px] text-[20px] font-bold text-[var(--textBlack100)] mb-4 ${className}`}>{title}</h3>
);

// export const DebtPaymentInfoCard: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
//   <div className='px-[24px] py-[12px] my-6 bg-white rounded-[16px] border-[var(--primaryColor500)] border'>
//     <div className='text-sm font-bold'>{title}</div>
//     <div className='text-sm text-[var(--textBlack56)]'>{subtitle}</div>
//   </div>
// );

export const DebtPaymentInfo: React.FC<DebtPaymentInfoProps> = ({
  debt,
  paymentMethod,
  setPaymentMethod,
  scheduleDate,
  setScheduleDate,
  title = 'THÔNG TIN THANH TOÁN',
  showPaymentMethods = true,
  // showPaymentButton = false,
  vatRate = 0.05,
  setPaymentAmount = () => {},
  disableEditing = false,
  saleOrderItems = [],
  inventoryDocumentItems = [],
  transactions = [],
}) => {
  const [form] = Form.useForm();
  const [rawPaymentAmount, setRawPaymentAmount] = useState<string>('');
  const [isQuickViewOpen, setIsQuickViewOpen] = useState<boolean>(false);
  const [isQuickViewTransactionOpen, setIsQuickViewTransactionOpen] = useState<boolean>(false);
  const handlePaymentMethodChange = (e: RadioChangeEvent): void => {
    const method = e.target.value as PaymentType;
    setPaymentMethod(method);
  };
  if (vatRate > 0) {
    console.log('');
  }
  const handleRawPaymentAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRawPaymentAmount(value);

    setPaymentAmount(value === '' ? null : parseFloat(value));
  };
  const handleScheduleDateChange = (date: Dayjs | null): void => {
    setScheduleDate(date);
  };
  React.useEffect(() => {
    if (debt.status?.code !== DebtStatusCode.WAITING) {
      const defaultMethod = debt.remainingAmount > 0 ? PaymentType.PARTIAL_PAYMENT_FLEX : PaymentType.FULL_PAYMENT;
      setPaymentMethod(defaultMethod);
      handleScheduleDateChange(dayjs(debt.deadline));
    }
  }, [debt.status?.code, setPaymentMethod, debt.remainingAmount, setScheduleDate, debt.deadline]);
  const renderPaymentOptions = () => {
    if (debt.status?.code === DebtStatusCode.WAITING) {
      return (
        <>
          <Radio
            value={PaymentType.FULL_PAYMENT}
            className='w-full rounded-xl p-4 py-2 !bg-white !border-[var(--textBlack12)] border'
          >
            <div>
              <div className='font-medium title-bold'>Thu hồi toàn bộ</div>
              <div className='text-sm text-[var(--textBlack56)]'>Thu hồi tổng số tiền nợ</div>
            </div>
          </Radio>
          <Radio
            value={PaymentType.PARTIAL_PAYMENT_FLEX}
            className='w-full !border-[var(--textBlack12)] !bg-white border rounded-xl p-4 py-2'
          >
            <div>
              <div className='font-medium title-bold'>Số tiền khác tùy chọn</div>
              <div className='text-sm text-[var(--textBlack56)]'>Thu hồi số tiền tùy chọn</div>
            </div>
          </Radio>
          <Radio
            value={PaymentType.NEGOTIATED_PAYMENT}
            className='w-full !border-[var(--textBlack12)] !bg-white border rounded-xl p-4 py-2'
          >
            <div className='w-full'>
              <div className='font-medium title-bold'>Gia hạn thêm</div>
              <div className='text-sm text-[var(--textBlack56)] mt-2'>
                <DatePicker
                  placeholder='Chọn thời gian gia hạn thêm'
                  className='w-full'
                  value={paymentMethod === PaymentType.NEGOTIATED_PAYMENT ? scheduleDate : null}
                  onChange={paymentMethod === PaymentType.NEGOTIATED_PAYMENT ? handleScheduleDateChange : undefined}
                  format='DD/MM/YYYY'
                  disabled={paymentMethod !== PaymentType.NEGOTIATED_PAYMENT || disableEditing}
                />
              </div>
            </div>
          </Radio>
        </>
      );
    } else {
      return (
        <Radio
          value={paymentMethod}
          className='w-full rounded-xl p-4 py-2 !bg-white !border-[var(--textBlack12)] border'
        >
          <div>
            <div className='font-medium title-bold'>
              {paymentMethod === PaymentType.PARTIAL_PAYMENT_FLEX ? 'Số tiền khác tùy chọn' : 'Thu hồi toàn bộ'}
            </div>

            <div className='text-sm text-[var(--textBlack56)]'>
              {paymentMethod !== PaymentType.PARTIAL_PAYMENT_FLEX
                ? 'Thu hồi tổng số tiền nợ'
                : 'Thu hồi số tiền tùy chọn'}
            </div>
          </div>
        </Radio>
      );
    }
  };

  return (
    <div className='rounded-[16px] flex flex-col justify-between '>
      <div>
        <DebtSectionHeader title={title} />

        {/* Payment Methods */}
        {showPaymentMethods && (
          <div className='mb-6'>
            <Radio.Group
              onChange={handlePaymentMethodChange}
              value={paymentMethod}
              disabled={disableEditing}
              className='w-full flex flex-col gap-3 custom-radio radio-border-primary mt-4 custom-group-radio'
            >
              {renderPaymentOptions()}
            </Radio.Group>
          </div>
        )}

        {/* Order Summary */}
        <div className='grid grid-cols-1 md:grid-cols-4 gap-4 gap-y-2 items-center'>
          <div className='text-sm text-[var(--textBlack100)]'>Số lượng</div>
          <div className='text-sm font-medium flex items-center gap-2 md:col-span-3'>
            <span className='flex gap-2 text-[var(--textBlack56)]'>
              <span className='font-medium text-[var(--textBlack100)]'>
                {debt.refTable === 'InventoryDocuments' ? inventoryDocumentItems.length : saleOrderItems.length}
              </span>{' '}
              sản phẩm
            </span>
            <span
              className='text-[var(--primaryColor500)] font-bold cursor-pointer'
              onClick={() => setIsQuickViewOpen(true)}
            >
              Nhấn để xem nhanh
            </span>
          </div>

          <span className='text-sm text-[var(--textBlack100)]'>Tạm tính</span>
          <span className='md:col-span-3 font-bold text-[var(--textBlack100)]'>
            {formatCurrency(debt?.totalAmount || 0)} <span className='text-[var(--textBlack56)]'>VND</span>
          </span>

          <hr className=' md:col-span-4' />

          {/* <span className='text-sm text-[var(--textBlack100)]'>VAT</span>
          <span className='md:col-span-3 font-medium text-[var(--textBlack100)]  h-[40px] flex items-center'>
            {vatRate * 100}% <span className='text-[var(--textBlack56)] mx-2'>~</span>{' '}
            {formatCurrency((debt?.debtAmount || 0) * vatRate)}
            <span className='ml-2 text-[var(--textBlack56)]'> VND</span>
          </span> */}

          <span className='text-sm text-[var(--textBlack100)]'>Mã giảm giá</span>
          <span className='md:col-span-3 font-medium text-[var(--textBlack100)] '>
            <span className='text-[var(--textBlack56)]'>0%</span>
          </span>

          <hr className=' md:col-span-4' />

          {/* <span className='text-sm text-[var(--textBlack100)] font-bold'>Tổng tiền</span>
          <span className='md:col-span-3 font-bold text-[var(--textBlack100)]'>
            {formatCurrency(debt?.debtAmount || 0)} <span className='text-[var(--textBlack56)]'>VND</span>
          </span> */}

          <span className='text-sm text-[var(--textBlack100)] font-bold'>Đã thanh toán</span>
          <span className='md:col-span-3 font-bold text-[var(--textBlack100)]'>
            {formatCurrency(debt?.paidAmount || 0)} <span className='text-[var(--textBlack56)]'>VND</span>{' '}
            <span className='italic'>(Không bao gồm tiền cọc)</span>
          </span>

          <span className='text-sm text-[var(--textBlack56)]'>Còn lại</span>
          <span className='md:col-span-3 font-bold text-[var(--textBlack100)]'>
            {formatCurrency(debt?.remainingAmount || 0)} <span className='text-[var(--textBlack56)]'>VND</span>
          </span>

          {paymentMethod === PaymentType.PARTIAL_PAYMENT_FLEX && (
            <Form form={form} className='md:col-span-4 grid grid-cols-1 md:grid-cols-4  '>
              <span className='text-sm text-[var(--textBlack100)]'>
                <LabelWithRequired label='Thu thêm' />
              </span>
              <Form.Item
                name='paymentAmount'
                rules={[{ required: true, message: 'Vui lý nhập số tiền' }]}
                className='md:col-span-3 text-[var(--textBlack100)] !mb-2'
              >
                <InputCommon
                  type='money'
                  value={rawPaymentAmount}
                  onChange={handleRawPaymentAmountChange}
                  placeholder='Nhập vào số tiền'
                  hardSize='sm'
                  className='!text-[var(--textBlack100)] font-bold'
                  disabled={debt.status?.code !== DebtStatusCode.WAITING}
                  max={debt?.remainingAmount || 0}
                />
              </Form.Item>
              <span className='text-sm text-[var(--textBlack100)]'>
                <LabelWithRequired label='Ngày thu' />
              </span>
              <Form.Item
                name='scheduleDate'
                rules={[{ required: true, message: 'Vui lý chọn ngày' }]}
                className='md:col-span-3 text-[var(--textBlack100)] !mb-2'
              >
                <DatePicker
                  placeholder={
                    debt.status?.code !== DebtStatusCode.WAITING
                      ? scheduleDate
                        ? scheduleDate.format('DD/MM/YYYY')
                        : 'Chọn ngày'
                      : 'Chọn ngày trả phần còn lại'
                  }
                  className='w-full '
                  value={paymentMethod === PaymentType.PARTIAL_PAYMENT_FLEX ? scheduleDate : null}
                  onChange={paymentMethod === PaymentType.PARTIAL_PAYMENT_FLEX ? handleScheduleDateChange : undefined}
                  format='DD/MM/YYYY'
                  disabled={debt.status?.code !== DebtStatusCode.WAITING}
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                />
              </Form.Item>
            </Form>
          )}
          <div className='md:col-span-4 flex items-center gap-2 mt-4'>
            <div
              className='text-sm text-[var(--primaryColor500)] font-bold cursor-pointer'
              onClick={() => setIsQuickViewTransactionOpen(true)}
            >
              Lịch sử thanh toán
            </div>
          </div>
        </div>
      </div>
      <QuickViewProductsModal
        isOpen={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
        products={debt.refTable === 'InventoryDocuments' ? inventoryDocumentItems : saleOrderItems}
        typeOrder={debt.refTable}
      />
      <QuickViewTransactionsModal
        isOpen={isQuickViewTransactionOpen}
        onClose={() => setIsQuickViewTransactionOpen(false)}
        transactions={transactions}
        title='LỊCH SỬ THANH TOÁN'
      />
    </div>
  );
};
